/**
 * Env-var-backed release flag registry. Single source of truth for all
 * production / release toggles previously evaluated against PostHog.
 *
 * **Why env vars and not PostHog:** PostHog feature-flag billing crosses the
 * paid-tier threshold for the marketing site, and flag evaluation latency
 * adds up across server-rendered pages. Release toggles change rarely; a
 * Vercel env-var change + redeploy is the right tradeoff. PostHog stays
 * for actual experiments (per-visitor bucketing, exposure tracking) — see
 * `EXPERIMENTS` in `lib/experiments.ts`.
 *
 * **Each flag has a fixed env var name.** The string flag key
 * (`"teams-page-enabled"`, etc.) is preserved so call sites in
 * server components, the Flags SDK `decide()` functions, and the sitemap
 * keep working unchanged. The registry below maps each key to its env var.
 *
 * **Validation is strict.** A missing or malformed value throws at first
 * read so a misconfigured Vercel project fails the build instead of
 * silently dark-launching every gated surface.
 */

// Release flags that have moved off PostHog. Anything not in this map still
// resolves through PostHog (see `getReleaseFlag` in `lib/posthog-server.ts`).
const RELEASE_FLAG_ENV_VARS = {
  "teams-page-enabled": "RELEASE_TEAMS_PAGE",
  "per-scope-chat-enabled": "RELEASE_PER_SCOPE_CHAT",
  "ai-assistant-enabled": "AI_ASSISTANT_ENABLED",
  "new-tests-pages": "RELEASE_NEW_TESTS_PAGES",
  "symptoms-enabled": "SYMPTOMS_ENABLED",
  "search-kb-fallback-enabled": "SEARCH_AI_ANSWER_ENABLED",
} as const

export type ReleaseFlagKey = keyof typeof RELEASE_FLAG_ENV_VARS

const RELEASE_FLAG_KEYS = Object.keys(RELEASE_FLAG_ENV_VARS) as ReleaseFlagKey[]

export function isReleaseFlagKey(key: string): key is ReleaseFlagKey {
  return key in RELEASE_FLAG_ENV_VARS
}

/**
 * Read a boolean release flag. Throws if the env var is unset or not
 * exactly `"true"` / `"false"` — fail-loud is the point.
 */
export function readReleaseFlag(key: ReleaseFlagKey): boolean {
  const envVar = RELEASE_FLAG_ENV_VARS[key]
  const raw = process.env[envVar]
  if (raw !== "true" && raw !== "false") {
    throw new Error(
      `Release flag "${key}" requires env var ${envVar} to be "true" or "false"; got ${raw === undefined ? "(unset)" : `"${raw}"`}.`
    )
  }
  return raw === "true"
}

/**
 * Multivariate rollout status used for country/locale gates. Same
 * semantics as the old `nl-status` / `fr-status` PostHog flags:
 *   - `disabled` = routes don't exist at all
 *   - `built`    = SSG'd but gated with 404 unless magic-link cookie set
 *   - `waitlist` = fully public + advertised (sitemap, hreflang, indexable),
 *                  but the market hasn't opened booking yet, so booking CTAs
 *                  open the waitlist modal instead of linking to the booking
 *                  app. "Launched as a waitlist site." Public like
 *                  `launched`, CTAs like `built` — it splits the old
 *                  public-vs-booking conflation that `launched` carried.
 *   - `checkup`  = public + advertised AND booking is live, but ONLY for the
 *                  blood check-up. The expansion products (full-body MRI +
 *                  its add-ons like DEXA, and Ahead Membership) aren't
 *                  available in the market yet, so their CTAs show a "coming
 *                  soon" pill and open the waitlist modal. "Launched on blood
 *                  only." Lets a market go live as a bookable site without
 *                  waiting for the MRI clinic / membership flow to be ready —
 *                  flip to `launched` per-product as each comes online.
 *   - `checkup-preview` = the full `checkup` experience (blood books, MRI +
 *                  membership coming-soon) but NOT public: still gated by the
 *                  proxy `?<key>-preview=<token>` magic-link cookie and absent
 *                  from sitemap/hreflang/index, exactly like `built`. Lets a
 *                  market dress-rehearse its blood-only launch behind the
 *                  preview token before flipping public to `checkup`.
 *   - `launched` = fully public + advertised AND booking is live for
 *                  everything (blood + MRI + membership)
 *
 * Unset / empty / `"disabled"` all collapse to `"disabled"` so a fresh
 * Vercel project without the env var defaults to "feature off". Anything
 * else throws.
 */
export type RolloutStatus =
  | "disabled"
  | "built"
  | "waitlist"
  | "checkup-preview"
  | "checkup"
  | "launched"

export function readRolloutStatus(
  envVar:
    | "NL_STATUS"
    | "FR_STATUS"
    | "DE_STATUS"
    | "AT_STATUS"
    | "QUIZ_FOCUS_STATUS"
): RolloutStatus {
  const raw = process.env[envVar]
  if (raw === undefined || raw === "" || raw === "disabled") return "disabled"
  if (
    raw === "built" ||
    raw === "waitlist" ||
    raw === "checkup-preview" ||
    raw === "checkup" ||
    raw === "launched"
  )
    return raw
  throw new Error(
    `${envVar}="${raw}" must be one of "disabled", "built", "waitlist", "checkup-preview", "checkup", "launched".`
  )
}

/**
 * Whether a rollout status makes the market PUBLIC — no token gate, in
 * sitemap/hreflang, indexable (the PUBLIC column of the status table). The
 * derived `*_PUBLIC` boolean encodes the same thing, but that's only forwarded
 * via `next.config` `env:` inlining (literal `process.env.*_PUBLIC` reads);
 * code that reads the status *dynamically* at runtime (e.g. the review
 * reachability gate) must derive it from `*_STATUS`, the only real env var.
 */
export function isPublicRolloutStatus(status: string | undefined): boolean {
  return status === "waitlist" || status === "checkup" || status === "launched"
}

/**
 * Country prefixes whose routes exist (the ENABLED column) — the `*_STATUS`-
 * derived twin of `knownCountryCodes` in `i18n/config.ts`, kept in the same
 * shape (`ch`/`nl` unconditional, `de`/`at` flag-gated).
 *
 * Exists because `next.config.ts` `redirects()` runs during config evaluation,
 * before the `env:` block forwards the derived `*_ENABLED` booleans that
 * `i18n/config.ts` reads — importing `knownCountryCodes` there would silently
 * drop DE. Runtime code should keep using `knownCountryCodes`.
 * `__tests__/german-domain.test.ts` asserts the two stay in sync.
 */
export function rolloutCountryCodes(): string[] {
  return [
    "ch",
    "nl",
    ...(readRolloutStatus("DE_STATUS") !== "disabled" ? ["de"] : []),
    ...(readRolloutStatus("AT_STATUS") !== "disabled" ? ["at"] : []),
  ]
}

/**
 * Unified AI-assistant lane gate (`AI_ASSISTANT_ENABLED`). Registered in
 * `RELEASE_FLAG_ENV_VARS` like `per-scope-chat-enabled`, so it also resolves
 * through `getReleaseFlag("ai-assistant-enabled")`. This typed accessor is the
 * fail-closed form for direct server imports + the `env:` forward in
 * `next.config.ts`: an unset or non-`"true"` value reads as `false`, so the
 * lane stays off unless explicitly turned on.
 */
export function aiAssistantEnabled(): boolean {
  return process.env.AI_ASSISTANT_ENABLED === "true"
}

/**
 * Stage 0 of the search AI answer (customer-support
 * docs/superpowers/specs/2026-08-07-search-ai-answer-design.md): KB retrieval
 * results on zero-result searches. Fail-closed like `aiAssistantEnabled` —
 * unset reads as off.
 */
export function searchKbFallbackEnabled(): boolean {
  return process.env.SEARCH_AI_ANSWER_ENABLED === "true"
}

export const RELEASE_FLAG_REGISTRY = {
  keys: RELEASE_FLAG_KEYS,
  envVars: RELEASE_FLAG_ENV_VARS,
} as const
